export const ROUTE_PATHS = {
  HOME: '/',
} as const;

export interface Service {
  id: string;
  title: string;
  description: string;
  icon: string;
}

export interface Project {
  id: string;
  period: string;
  type: string;
  description: string;
  location: string;
  client: string;
}

export interface FleetItem {
  id: string;
  name: string;
  count: number;
  icon: string;
}

export interface Certification {
  id: string;
  name: string;
  issuer: string;
  expiryDate?: string;
  certificateNumber?: string;
  scope?: string;
}

export interface Award {
  id: string;
  title: string;
  issuer: string;
  date: string;
  description: string;
}

export interface Milestone {
  id: string;
  year: string;
  title: string;
  description: string;
}

export interface Registration {
  id: string;
  name: string;
  number: string;
}

export interface EmergencyContact {
  id: string;
  title: string;
  name: string;
  phone: string;
}

export const SERVICES_DATA: Service[] = [
  {
    id: 'oil-gas-construction',
    title: 'Oil & Gas Well Site Construction',
    description: 'Wellsite preparation, construction, and access roads for drilling and workover operations across all Saudi Arabia. Expert execution of site grading, access roads, and facility installations to support Saudi Aramco\'s 36+ active rigs nationwide.',
    icon: 'Drill',
  },
  {
    id: 'access-road',
    title: 'Access Road Construction & Repair',
    description: 'High-quality access roads, skid roads, and repair works for remote oil field operations. Engineered for extreme load capacity and harsh desert conditions with proper drainage and safety systems.',
    icon: 'Route',
  },
  {
    id: 'civil-earthworks',
    title: 'Civil & Earthworks',
    description: 'Excavation, backfilling, right-of-way clearing, berm construction, and pipeline civil works. Large-scale earthmoving and land preparation specialized for desert terrain and industrial facilities.',
    icon: 'Mountain',
  },
  {
    id: 'heavy-equipment',
    title: 'Heavy Equipment & Transportation',
    description: 'Fleet of 500+ machines: Dozers, Loaders, Graders, Dump Trucks, Vacuum Tankers, and Heavy Transport. Available for contract operations with certified, experienced operators.',
    icon: 'Truck',
  },
  {
    id: 'pipeline-construction',
    title: 'Pipeline Construction',
    description: 'Excavation, installation, and construction support for oil, gas, and water pipeline projects. Full trenching, welding, coating, and testing services in compliance with Saudi Aramco and international standards.',
    icon: 'Pipe',
  },
  {
    id: 'sand-removal',
    title: 'Environmental & Sand Removal',
    description: 'Sand removal from flowline pipes for water wells and gas wells across SDGM, HWYH, UTMN, and QURAYYA Plants. Specialized desert sand management and dune clearance operations.',
    icon: 'Wind',
  },
  {
    id: 'maintenance',
    title: 'Maintenance & Installation',
    description: 'Water well maintenance, irrigation systems, industrial machinery installation, bridges, tunnels, and infrastructure works. Professional rigging, lifting, commissioning, and precision alignment services.',
    icon: 'Settings',
  },
];

export const ONGOING_PROJECTS: Project[] = [
  {
    id: 'og-1',
    period: '2019 – Present',
    type: 'Main Contract',
    description: 'Wellsite Construction & Access Roads Repair — Contract #6600044425',
    location: 'All KSA',
    client: 'SAUDI ARAMCO',
  },
  {
    id: 'og-2',
    period: '2024 – Present',
    type: 'Main Contract',
    description: 'Sand Removal from Flowline Pipe — Water Wells (P.O#6511185253)',
    location: 'SDGM, HWYH, UTMN, QURAYYA',
    client: 'SAUDI ARAMCO',
  },
  {
    id: 'og-3',
    period: '2024 – Present',
    type: 'Main Contract',
    description: 'Sand Removal from Flowline Pipe — Gas Wells (P.O#6511185253)',
    location: 'ANDR, SDGM',
    client: 'SAUDI ARAMCO',
  },
  {
    id: 'og-4',
    period: '2025 – Present',
    type: 'Main Contract',
    description: 'Access Roads Maintenance & Repair — All Saudi Aramco Facilities',
    location: 'All KSA',
    client: 'SAUDI ARAMCO',
  },
  {
    id: 'og-5',
    period: '2025 – Present',
    type: 'Main Contract',
    description: 'Civil Works & Earthmoving — Industrial Facilities Support',
    location: 'Eastern Province',
    client: 'SAUDI ARAMCO',
  },
];

export const COMPLETED_PROJECTS: Project[] = [
  {
    id: 'cp-1',
    period: '2007 – 2010',
    type: 'Subcontract',
    description: 'Excavation, Backfilling, Right-of-Way & Berm Pipeline',
    location: 'Khurais',
    client: 'FMQ',
  },
  {
    id: 'cp-2',
    period: '2010 – 2013',
    type: 'Subcontract',
    description: 'Excavation, Backfilling, Construction of Right-of-Way',
    location: 'Khurais',
    client: 'AL-SHALAWI',
  },
  {
    id: 'cp-3',
    period: '2015 – 2019',
    type: 'Main Contract',
    description: 'Wellsite Construction & Access Roads — Contract #6600034539',
    location: 'All KSA',
    client: 'SAUDI ARAMCO',
  },
  {
    id: 'cp-4',
    period: '2017',
    type: 'Subcontract',
    description: 'Excavation, Backfilling, R-O-W & Berm Pipeline (Mazalij/Abufifan)',
    location: 'Khurais/Aufian',
    client: 'ARKAD ENGINEERING',
  },
  {
    id: 'cp-5',
    period: '2023',
    type: 'Subcontract',
    description: 'Skid Road Construction Project',
    location: 'RAK Area',
    client: 'HALLIBURTON',
  },
  {
    id: 'cp-6',
    period: '2022',
    type: 'Main Contract',
    description: 'Pipeline Civil Works — Mazalij Field Development',
    location: 'Mazalij',
    client: 'SAUDI ARAMCO',
  },
  {
    id: 'cp-7',
    period: '2021',
    type: 'Main Contract',
    description: 'Water Well Maintenance & Irrigation Systems',
    location: 'Al-Hasa Region',
    client: 'SAUDI ARAMCO',
  },
  {
    id: 'cp-8',
    period: '2020',
    type: 'Subcontract',
    description: 'Heavy Equipment Support — Drilling Operations',
    location: 'Ghawar Field',
    client: 'BAKER HUGHES',
  },
  {
    id: 'cp-9',
    period: '2019',
    type: 'Main Contract',
    description: 'Access Roads & Site Preparation — Multiple Wellsites',
    location: 'All KSA',
    client: 'SAUDI ARAMCO',
  },
  {
    id: 'cp-10',
    period: '2018',
    type: 'Subcontract',
    description: 'Environmental Remediation & Sand Management',
    location: 'UTMN Plant',
    client: 'SAUDI ARAMCO',
  },
];

export const FLEET_EQUIPMENT: FleetItem[] = [
  { id: 'eq-1', name: 'DOZER', count: 121, icon: 'Tractor' },
  { id: 'eq-2', name: 'LOADER', count: 72, icon: 'Container' },
  { id: 'eq-3', name: 'GRADER', count: 45, icon: 'Gauge' },
  { id: 'eq-4', name: 'BACK HOE', count: 26, icon: 'Shovel' },
  { id: 'eq-5', name: 'ROLLER', count: 53, icon: 'CircleDot' },
  { id: 'eq-6', name: 'DUMP TRUCK', count: 171, icon: 'Truck' },
  { id: 'eq-7', name: 'LOW BED / TRAILER', count: 34, icon: 'ArrowLeftRight' },
  { id: 'eq-8', name: 'DUMPER', count: 31, icon: 'Truck' },
  { id: 'eq-9', name: 'VACUUM TANKER', count: 75, icon: 'Droplets' },
  { id: 'eq-10', name: 'WATER TANKER', count: 53, icon: 'Droplets' },
];

export const FLEET_PERSONNEL = {
  totalTeam: 305,
  engineers: 11,
  hseTeam: 5,
  surveyors: 21,
  workPermitReceivers: 17,
  fieldSupervisors: 36,
};

export const CERTIFICATIONS_DATA: Certification[] = [
  {
    id: 'cert-1',
    name: 'ISO 9001:2015 — Quality Management System',
    issuer: 'International Certification Body',
    expiryDate: 'Jan 2027',
    certificateNumber: 'KAQM202401075',
    scope: 'Quality Management System for construction and heavy equipment services in oil & gas sector.',
  },
  {
    id: 'cert-2',
    name: 'ISO 14001:2015 — Environmental Management System',
    issuer: 'International Certification Body',
    expiryDate: 'Jan 2027',
    certificateNumber: 'KAEM202401039',
    scope: 'Environmental Management System for construction operations and fleet management.',
  },
  {
    id: 'cert-3',
    name: 'ISO 45001:2018 — Occupational Health & Safety',
    issuer: 'International Certification Body',
    expiryDate: 'Jan 2027',
    certificateNumber: 'KAOH202401051',
    scope: 'Occupational Health & Safety Management System for construction and heavy machinery operations.',
  },
  {
    id: 'cert-4',
    name: 'SACS-002 — Saudi Aramco Cybersecurity',
    issuer: 'Saudi Aramco',
    expiryDate: 'Dec 2027',
    certificateNumber: 'CCC Ref: 07-1738-121725-R',
    scope: 'Cybersecurity compliance for IT systems and operational technology in contractor operations.',
  },
];

export const AWARDS_DATA: Award[] = [
  {
    id: 'award-1',
    title: 'Historical Wellsite Milestone',
    issuer: 'Saudi Aramco — WSD',
    date: '2023',
    description: 'Most wellsites constructed in a single year.',
  },
  {
    id: 'award-2',
    title: 'Certificate of Appreciation — Near Miss',
    issuer: 'Saudi Aramco — WSD',
    date: 'May 2023',
    description: 'Near Miss recognition — SLS-4314680',
  },
  {
    id: 'award-3',
    title: 'Certificate of Appreciation — Critical Near Miss',
    issuer: 'Saudi Aramco — WSD',
    date: 'Nov 2023',
    description: 'Critical Near Miss recognition — SLS-5172715',
  },
  {
    id: 'award-4',
    title: 'Certificate of Appreciation — Critical Near Miss',
    issuer: 'Saudi Aramco — WSD',
    date: 'Feb 2024',
    description: 'Critical Near Miss recognition — SLS-5506558',
  },
  {
    id: 'award-5',
    title: 'JSAs Workshop Excellence',
    issuer: 'Saudi Aramco — SAWCD',
    date: 'Dec 2024',
    description: 'JSAs Revision & Enhancement Workshop',
  },
  {
    id: 'award-6',
    title: 'Journey Management Award',
    issuer: 'Saudi Aramco — SAWCD',
    date: 'Nov 2024',
    description: 'Outstanding Journey Management System',
  },
  {
    id: 'award-7',
    title: 'Safety Campaign Certificate',
    issuer: 'Saudi Aramco — WCD',
    date: 'Jun 2025',
    description: 'WCD Q2-25 Interactive Safety Campaign',
  },
];

export const MILESTONES_DATA: Milestone[] = [
  {
    id: 'ms-1',
    year: '2008',
    title: 'Company Founded',
    description: '100% Saudi Establishment — Rboua Alsman Contracting Establishment founded in Al-Hasa, Eastern Province.',
  },
  {
    id: 'ms-2',
    year: '2015',
    title: 'Main Contract with Saudi Aramco',
    description: 'Wellsite Construction & Access Roads — Contract #6600034539 for 36 Rigs nationwide.',
  },
  {
    id: 'ms-3',
    year: '2017',
    title: 'Mazalij & Abufifan Pipeline',
    description: 'Completed Mazalij & Abufifan Pipeline Project with Arkad Engineering.',
  },
  {
    id: 'ms-4',
    year: '2019',
    title: 'Renewed Saudi Aramco Main Contract',
    description: 'Renewed Main Contract #6600044425 — Wellsite Construction & Access Roads Repair, All KSA.',
  },
  {
    id: 'ms-5',
    year: '2023',
    title: 'Saudi Aramco Historical Wellsite Milestone Award',
    description: 'Received award for most wellsites constructed in a single year from Saudi Aramco WSD.',
  },
  {
    id: 'ms-6',
    year: '2024',
    title: 'ISO Triple Certification & SACS-002',
    description: 'ISO 9001, 14001 & 45001 Triple Certification · SACS-002 Cybersecurity Compliant.',
  },
  {
    id: 'ms-7',
    year: '2025',
    title: 'Sand Removal Contracts',
    description: 'Sand Removal Contracts secured · Safety Campaign Certificate of Appreciation received from WCD.',
  },
];

export const REGISTRATIONS_DATA: Registration[] = [
  {
    id: 'reg-1',
    name: 'Commercial Registration (CR)',
    number: '1010246217',
  },
  {
    id: 'reg-2',
    name: 'VAT Registration',
    number: '300109769700003',
  },
  {
    id: 'reg-3',
    name: 'Zakat Certificate',
    number: 'GAZT Compliant',
  },
];

export const CONTACT_INFO = {
  companyName: 'Rboua Alsman Contracting Establishment',
  arabicName: 'مؤسسة ربوع الأسمان للمقاولات',
  address: 'Alghawbia Agricultural Region\nAl Hofuf, Al-Hasa 36373\nKingdom of Saudi Arabia',
  phone: '+966 53 524 4128',
  email: 'info@robaalsman.com',
  website: 'www.robaalsman.com',
  poBox: '231129, Al Mubarraz 31982',
  gps: '25°18\'53.1"N, 49°39\'32.2"E',
  cr: '1010246217',
  vendorNo: '10047999',
  vat: '300109769700003',
  sca: '251625164',
};

export const EMERGENCY_CONTACTS: EmergencyContact[] = [
  {
    id: 'em-1',
    title: 'Fire Marshal',
    name: 'Hammod',
    phone: '0531148387',
  },
  {
    id: 'em-2',
    title: 'Fire Warden',
    name: 'Khalid',
    phone: '0570486943',
  },
  {
    id: 'em-3',
    title: 'Saudi Emergency',
    name: '',
    phone: '911',
  },
  {
    id: 'em-4',
    title: 'Aramco Security NARSCC',
    name: '',
    phone: '013 572-0911',
  },
];

export const STATS = [
  {
    id: 'stat-1',
    value: 2008,
    label: 'Founded',
    suffix: '',
  },
  {
    id: 'stat-2',
    value: 17,
    label: 'Years Experience',
    suffix: '+',
  },
  {
    id: 'stat-3',
    value: 305,
    label: 'Team Members',
    suffix: '+',
  },
  {
    id: 'stat-4',
    value: 500,
    label: 'Heavy Machines',
    suffix: '+',
  },
];